import { Stack, Typography } from '@mui/material';

import { ExportConnectionEnum } from '@verantos-dev/data-access-export-connections';
import { Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { ExportConnectionDetailsType } from '@verantos-dev/types-cohorts';

import { getExportLocationDisplayText } from '../../hooks/useExportCohortModal';
import { useExportCohortModalStrings } from '../../hooks/useExportCohortModalStrings';

type ExportCohortModalSubmittedProps = {
  projectName?: string;
  cohortName?: string;
  schemaName?: string;
  fileName?: string;
  fileType?: string;
  selectedExportLocation?: ExportConnectionDetailsType;
};

export const ExportCohortModalSubmitted: React.FC<ExportCohortModalSubmittedProps> = ({
  projectName,
  cohortName,
  schemaName,
  fileName,
  fileType,
  selectedExportLocation,
}) => {
  const { contentMessages } = useExportCohortModalStrings();

  return (
    <Stack marginTop={4} display={'flex'} gap={4} direction={'column'}>
      <Stack direction={'row'} gap={4} width={'100%'}>
        <Stack direction={'column'} gap={3} flex={1}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            fontSize={'12px'}
            color={theme.palette.text.primary}
            style={{ textTransform: 'uppercase' }}
          >
            {contentMessages.project}
          </Typography>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-project`} icon={'project'} width={20} height={20} />
            <Stack>
              <Typography fontSize={'14px'} color={theme.palette.text.primary}>
                {projectName}
              </Typography>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.projectName}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction={'column'} gap={3} flex={1}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            fontSize={'12px'}
            color={theme.palette.text.primary}
            style={{ textTransform: 'uppercase' }}
          >
            {contentMessages.cohort}
          </Typography>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-cohort`} icon={'cohort'} width={20} height={20} />
            <Stack>
              <Typography fontSize={'14px'} color={theme.palette.text.primary}>
                {cohortName}
              </Typography>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.cohortName}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction={'column'} gap={3}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          fontSize={'12px'}
          color={theme.palette.text.primary}
          style={{ textTransform: 'uppercase' }}
        >
          {contentMessages.location}
        </Typography>
        <Stack direction={'column'} gap={3}>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-connector`} icon={'connector'} width={20} height={20} />
            <Stack direction={'column'}>
              <Typography fontSize={'14px'} color={theme.palette.text.primary}>
                {selectedExportLocation ? getExportLocationDisplayText(selectedExportLocation) : ''}
              </Typography>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.exportLocation}
              </Typography>
            </Stack>
          </Stack>
          {selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE && (
            <Stack direction={'row'} gap={2} maxWidth={'100%'}>
              <Icon id={`exportSuccess-schema`} icon={'schema'} width={20} height={20} />
              <Stack direction={'column'} width={'90%'}>
                <Typography
                  fontSize={'14px'}
                  color={theme.palette.text.primary}
                  overflow={'hidden'}
                  textOverflow={'ellipsis'}
                  whiteSpace={'nowrap'}
                >
                  {schemaName}
                </Typography>
                <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                  {contentMessages.schemaName}
                </Typography>
              </Stack>
            </Stack>
          )}
          {selectedExportLocation?.connectionType === ExportConnectionEnum.S3 && (
            <>
              <Stack direction={'row'} gap={2} maxWidth={'100%'}>
                <Icon id={`exportSuccess-filePath`} icon={'filePath'} width={20} height={20} />
                <Stack direction={'column'} width={'90%'}>
                  <Typography
                    fontSize={'14px'}
                    color={theme.palette.text.primary}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                  >
                    {fileName}
                  </Typography>
                  <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                    {contentMessages.path}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction={'row'} gap={2} maxWidth={'100%'}>
                <Icon id={`exportSuccess-fileType`} icon={'fileType'} width={20} height={20} />
                <Stack direction={'column'} width={'90%'}>
                  <Typography fontSize={'14px'} color={theme.palette.text.primary}>
                    {fileType}
                  </Typography>
                  <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                    {contentMessages.fileType}
                  </Typography>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
